import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useExperienceNames } from '@/hooks/useExperiences';
import { usePartnerActivity } from '@/hooks/usePartnerActivity';
import type { ActivityType } from '@/types/types_activity';
import { formatDate } from '@/util/util_format';

const cardClass = 'tw:rounded-lg tw:border tw:bg-card tw:p-6';
const cardTitleClass = 'tw:text-sm tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase';
const cardBlurbClass = 'tw:mt-1 tw:mb-5 tw:text-sm tw:text-muted-foreground';
const listClass = 'tw:divide-y tw:divide-ed-umber/10';
const rowClass = 'tw:flex tw:items-baseline tw:justify-between tw:gap-4 tw:py-2 tw:first:pt-0 tw:last:pb-0';
const emptyClass = 'tw:py-2 tw:text-sm tw:text-ed-stone';

// One series, so the chart carries one colour and needs no legend — the heading
// names what the bars are. Umber rather than the brand gold because a bar is a
// graphical object a reader has to make out against the white card, and gold at
// this size does not hold up.
const barColor = 'var(--color-ed-umber)';
const barHoverColor = 'var(--color-ed-gold)';

type BucketUnitType = 'day' | 'week' | 'month';

interface RangeOptionType {
  key: string;
  label: string;
  /** How far back the window reaches, or null for everything on record. */
  days: number | null;
  bucket: BucketUnitType;
}

/**
 * The windows on offer, and how wide a bar is in each.
 *
 * The bucket widens with the window because the chart is one fixed width: 365
 * daily bars across it are sub-pixel noise, and "all time" has no length to plan
 * for at all. Weeks for a year and months beyond it keep a bar readable however
 * long the partner has been with us.
 */
const ranges: RangeOptionType[] = [
  { key: '7', label: '7 days', days: 7, bucket: 'day' },
  { key: '30', label: '30 days', days: 30, bucket: 'day' },
  { key: '90', label: '90 days', days: 90, bucket: 'day' },
  { key: '365', label: 'Year', days: 365, bucket: 'week' },
  { key: 'all', label: 'All time', days: null, bucket: 'month' },
];

/**
 * A ceiling on how many bars can be drawn.
 *
 * `createdAt` comes off the client's clock, so one machine set to 1999 would
 * otherwise ask this to render three hundred years of empty months. Enough for
 * a decade of monthly bars, which the range picker cannot exceed honestly.
 */
const maxBuckets = 240;

// Chart geometry, in user units — the SVG scales to the card via its viewBox.
const chart = { width: 720, height: 170, top: 14, right: 6, bottom: 22, left: 30 };
const plotWidth = chart.width - chart.left - chart.right;
const plotHeight = chart.height - chart.top - chart.bottom;

/** Midnight starting the day, week or month an instant falls in, locally. */
function startOfBucket(date: Date, unit: BucketUnitType): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  if (unit === 'week') {
    // Monday-based: getDay() calls Sunday 0, which belongs to the week before.
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  }
  if (unit === 'month') start.setDate(1);
  return start;
}

function nextBucket(date: Date, unit: BucketUnitType): Date {
  const next = new Date(date);
  if (unit === 'day') next.setDate(next.getDate() + 1);
  if (unit === 'week') next.setDate(next.getDate() + 7);
  if (unit === 'month') next.setMonth(next.getMonth() + 1);
  return next;
}

/** A bucket's identity: the local calendar date it starts on, as `yyyy-mm-dd`. */
function bucketKey(date: Date): string {
  return date.toLocaleDateString('en-CA');
}

const dayLabelFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const monthLabelFormatter = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' });

function formatBucket(date: Date, unit: BucketUnitType): string {
  return unit === 'month' ? monthLabelFormatter.format(date) : dayLabelFormatter.format(date);
}

function describeBucket(date: Date, unit: BucketUnitType, count: number): string {
  const period = unit === 'week' ? `Week of ${formatBucket(date, unit)}` : formatBucket(date, unit);
  return `${period} — ${count} ${count === 1 ? 'view' : 'views'}`;
}

interface BucketType {
  key: string;
  date: Date;
  count: number;
}

/**
 * One bucket per period in the window, oldest first, quiet periods included as
 * zeroes — a gap is as much a part of the picture as a spike.
 *
 * "All time" starts at the oldest event held rather than at a fixed offset,
 * which is what makes the axis say when the partner actually started.
 */
function bucketEvents(events: ActivityType[], range: RangeOptionType): BucketType[] {
  const counts = new Map<string, number>();
  for (const event of events) {
    const key = bucketKey(startOfBucket(new Date(event.createdAt), range.bucket));
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  // Events arrive newest first, so the last one is the oldest held.
  const oldest = events.at(-1);
  const from = new Date();
  if (range.days === null) {
    if (oldest) from.setTime(new Date(oldest.createdAt).getTime());
  } else {
    from.setDate(from.getDate() - (range.days - 1));
  }

  const end = startOfBucket(new Date(), range.bucket);
  const buckets: BucketType[] = [];
  let cursor = startOfBucket(from, range.bucket);
  while (cursor <= end && buckets.length < maxBuckets) {
    const key = bucketKey(cursor);
    buckets.push({ key, date: cursor, count: counts.get(key) ?? 0 });
    cursor = nextBucket(cursor, range.bucket);
  }
  return buckets;
}

/** Counts by some key, largest first. */
function tally<T>(items: T[], keyOf: (item: T) => string | undefined): { key: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = keyOf(item);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

interface PartnerActivityCardProps {
  partnerId: string;
}

/**
 * What one partner has been looking at, over a week, a month, a quarter, a year,
 * or their whole time with us.
 *
 * The three summary figures answer the questions that get asked about a
 * partner — are they using it at all, are they looking at the experiences
 * rather than just the front page, and when did they last visit — and the chart
 * below says whether that is one afternoon of browsing or a habit. See
 * `src/types/types_activity.ts` for what is and is not recorded.
 */
export function PartnerActivityCard({ partnerId }: PartnerActivityCardProps) {
  const [range, setRange] = useState<RangeOptionType>(ranges[1]);
  const [hovered, setHovered] = useState<number | null>(null);
  const { events, loading, error, truncated } = usePartnerActivity(partnerId, range.days);

  const buckets = useMemo(() => bucketEvents(events, range), [events, range]);
  const topExperiences = useMemo(
    () => tally(events.filter((e) => e.action === 'experienceView'), (e) => e.targetId).slice(0, 5),
    [events],
  );
  const byAccount = useMemo(() => tally(events, (e) => e.userEmail), [events]);

  // Only the handful of ids actually on screen get a name looked up — see
  // `useExperienceNames`. The whole catalogue, subscribed to live, is a great
  // deal of document for five list rows.
  const experienceNames = useExperienceNames(useMemo(() => topExperiences.map((t) => t.key), [topExperiences]));

  const experienceViews = events.filter((e) => e.action === 'experienceView').length;
  // Events arrive newest first, so the first one is the most recent visit.
  const lastSeen = events[0]?.createdAt;

  const maxCount = Math.max(...buckets.map((b) => b.count), 1);
  const columnWidth = plotWidth / Math.max(buckets.length, 1);
  // A 2px gap between neighbouring bars, so a run of busy periods reads as
  // separate periods rather than one block.
  const barWidth = Math.max(columnWidth - 2, 1);
  const yOf = (count: number) => chart.top + plotHeight - (count / maxCount) * plotHeight;

  // First, middle and last only: enough to place the run in time without the
  // labels colliding at 90 bars.
  const tickIndexes = [...new Set([0, Math.floor((buckets.length - 1) / 2), buckets.length - 1])];
  const hoveredBucket = hovered === null ? null : buckets[hovered];

  return (
    <section className={cardClass}>
      <div className='tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-3'>
        <div>
          <h2 className={cardTitleClass}>Activity</h2>
          <p className={cardBlurbClass}>
            Pages this partner’s accounts have opened. Our own browsing is not counted.
          </p>
        </div>
        <div className='tw:flex tw:flex-wrap tw:gap-1'>
          {ranges.map((option) => (
            <Button
              key={option.key}
              type='button'
              size='xs'
              variant={option.key === range.key ? 'default' : 'outline'}
              onClick={() => {
                setRange(option);
                setHovered(null);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {error && <p className='tw:mb-4 tw:text-sm tw:text-ed-red'>{error}</p>}

      {loading ? (
        <p className={emptyClass}>Loading…</p>
      ) : (
        <>
          <dl className='tw:mb-6 tw:grid tw:grid-cols-3 tw:gap-4'>
            <div>
              <dd className='tw:font-serif tw:text-3xl tw:font-light'>{events.length}</dd>
              <dt className='tw:text-xs tw:text-ed-stone'>Page views</dt>
            </div>
            <div>
              <dd className='tw:font-serif tw:text-3xl tw:font-light'>{experienceViews}</dd>
              <dt className='tw:text-xs tw:text-ed-stone'>Experience views</dt>
            </div>
            <div>
              <dd className='tw:font-serif tw:text-3xl tw:font-light'>{formatDate(lastSeen)}</dd>
              <dt className='tw:text-xs tw:text-ed-stone'>Last visit</dt>
            </div>
          </dl>

          {truncated && (
            <p className='tw:mb-4 tw:text-xs tw:text-ed-stone'>
              Showing the most recent {events.length.toLocaleString()} events — anything older than the
              first bar is not included.
            </p>
          )}

          {events.length === 0 ? (
            <p className={emptyClass}>
              Nothing recorded in this window. Activity is logged from the moment a partner account
              signs in — a partner with no accounts yet will always read empty.
            </p>
          ) : (
            <>
              {/* Reads out the hovered period. Kept above the chart, in a fixed-height
                  row, so pointing at a bar does not move the bars. */}
              <p className='tw:mb-1 tw:h-5 tw:text-xs tw:text-ed-stone'>
                {hoveredBucket ? describeBucket(hoveredBucket.date, range.bucket, hoveredBucket.count) : ''}
              </p>
              <svg
                viewBox={`0 0 ${chart.width} ${chart.height}`}
                width='100%'
                role='img'
                aria-label={`Page views by ${range.bucket}, ${range.label.toLowerCase()}`}
                className='tw:overflow-visible'
                onMouseLeave={() => setHovered(null)}
              >
                {/* Scale: the peak and the baseline, nothing in between. */}
                <line
                  x1={chart.left}
                  y1={chart.top}
                  x2={chart.width - chart.right}
                  y2={chart.top}
                  stroke='var(--color-ed-umber)'
                  strokeOpacity={0.15}
                />
                <line
                  x1={chart.left}
                  y1={chart.top + plotHeight}
                  x2={chart.width - chart.right}
                  y2={chart.top + plotHeight}
                  stroke='var(--color-ed-umber)'
                  strokeOpacity={0.3}
                />
                <text x={chart.left - 6} y={chart.top + 4} textAnchor='end' fontSize='10' fill='var(--color-ed-stone)'>
                  {maxCount}
                </text>
                <text
                  x={chart.left - 6}
                  y={chart.top + plotHeight + 4}
                  textAnchor='end'
                  fontSize='10'
                  fill='var(--color-ed-stone)'
                >
                  0
                </text>

                {buckets.map((bucket, i) => {
                  const x = chart.left + i * columnWidth;
                  const y = yOf(bucket.count);
                  return (
                    <g key={bucket.key}>
                      {bucket.count > 0 && (
                        <rect
                          x={x}
                          y={y}
                          width={barWidth}
                          height={chart.top + plotHeight - y}
                          rx={Math.min(2, barWidth / 2)}
                          fill={hovered === i ? barHoverColor : barColor}
                        />
                      )}
                      {/* Full-height hit area: a one-view day is a 2px bar, which
                          is not something anyone can point at. */}
                      <rect
                        x={x}
                        y={chart.top}
                        width={Math.max(columnWidth, 1)}
                        height={plotHeight}
                        fill='transparent'
                        onMouseEnter={() => setHovered(i)}
                      >
                        <title>{describeBucket(bucket.date, range.bucket, bucket.count)}</title>
                      </rect>
                    </g>
                  );
                })}

                {tickIndexes.map((i) => (
                  <text
                    key={i}
                    x={chart.left + i * columnWidth + barWidth / 2}
                    y={chart.height - 6}
                    textAnchor={i === 0 ? 'start' : i === buckets.length - 1 ? 'end' : 'middle'}
                    fontSize='10'
                    fill='var(--color-ed-stone)'
                  >
                    {formatBucket(buckets[i].date, range.bucket)}
                  </text>
                ))}
              </svg>

              <div className='tw:mt-6 tw:grid tw:gap-6 tw:sm:grid-cols-2'>
                <div>
                  <h3 className={cardTitleClass}>Most viewed</h3>
                  <ul className={`tw:mt-3 ${listClass}`}>
                    {topExperiences.length === 0 && <li className={emptyClass}>No experience pages opened.</li>}
                    {topExperiences.map(({ key, count }) => (
                      <li key={key} className={rowClass}>
                        <span className='tw:truncate tw:text-sm'>{experienceNames.get(key) ?? key}</span>
                        <span className='tw:text-sm tw:text-ed-stone'>{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className={cardTitleClass}>By account</h3>
                  <ul className={`tw:mt-3 ${listClass}`}>
                    {byAccount.map(({ key, count }) => (
                      <li key={key} className={rowClass}>
                        <span className='tw:truncate tw:text-sm'>{key}</span>
                        <span className='tw:text-sm tw:text-ed-stone'>{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
